/**
 * Формы. Каждый шаблон — свой скелет + css.
 *  • card     — все поля на одном экране (Tilda/Flexbe/Jotform).
 *  • stepper  — один шаг на экран, прогресс (Marquiz/Typeform).
 *  • callback — компактный «обратный звонок»: только телефон (Envybox).
 */
import type { FormContent, RuntimeWidgetConfig } from '@inbx/shared/schemas';

import { clear, el } from '../dom';
import { isValidEmail, isValidPhone, maskPhone } from '../validation';

import type { BuildCtx, BuildInstance, WidgetTemplate } from './types';

interface Field {
  key: string;
  type: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  options?: Array<string | { value: string; label: string }>;
  hint?: string;
}

function optPairs(o: Field['options']): Array<{ value: string; label: string }> {
  return (o ?? []).map((x) => (typeof x === 'string' ? { value: x, label: x } : x));
}

/** Проверка значения поля. null — всё в порядке, строка — текст ошибки. */
function validateField(field: Field, value: unknown): string | null {
  const empty =
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);
  if (field.required && empty) return 'Заполните это поле';
  if (empty) return null;
  if (field.type === 'email' && !isValidEmail(String(value))) return 'Введите корректный e-mail';
  if (field.type === 'phone' && !isValidPhone(String(value))) return 'Введите телефон полностью';
  return null;
}

/** Сбросить/показать ошибку под полем. */
function setFieldError(root: ParentNode, key: string, msg: string | null): void {
  const wrap = root.querySelector<HTMLElement>(`[data-field-key="${key}"]`);
  if (!wrap) return;
  wrap.classList.toggle('w-field--error', !!msg);
  let m = wrap.querySelector<HTMLElement>('.w-field-msg');
  if (msg) {
    if (!m) {
      m = el('div', 'w-field-msg');
      wrap.appendChild(m);
    }
    m.textContent = msg;
  } else if (m) {
    m.remove();
  }
}

function renderField(field: Field, values: Record<string, unknown>): HTMLElement {
  const wrap = el('div', 'w-field');
  wrap.dataset.fieldKey = field.key;
  const isChoice = field.type === 'radio' || field.type === 'checkbox';
  if (isChoice) wrap.classList.add('w-field--choice');
  const clearErr = (): void => {
    wrap.classList.remove('w-field--error');
    wrap.querySelector('.w-field-msg')?.remove();
  };

  const label = el('label', undefined, field.label);
  if (field.required) label.appendChild(el('span', 'w-required', ' *'));
  wrap.appendChild(label);

  let control: HTMLElement;
  switch (field.type) {
    case 'textarea': {
      const ta = el('textarea', 'w-input');
      ta.rows = 3;
      if (field.placeholder) ta.placeholder = field.placeholder;
      ta.value = (values[field.key] as string) ?? '';
      ta.addEventListener('input', () => {
        values[field.key] = ta.value;
        clearErr();
      });
      control = ta;
      break;
    }
    case 'select': {
      const sel = el('select', 'w-input');
      const empty = el('option', undefined, field.placeholder || '— выбрать —');
      empty.value = '';
      sel.appendChild(empty);
      for (const o of optPairs(field.options)) {
        const op = el('option', undefined, o.label);
        op.value = o.value;
        sel.appendChild(op);
      }
      sel.value = (values[field.key] as string) ?? '';
      sel.addEventListener('change', () => {
        values[field.key] = sel.value;
        clearErr();
      });
      control = sel;
      break;
    }
    case 'radio': {
      const g = el('div', 'w-radio-group');
      for (const o of optPairs(field.options)) {
        const l = el('label', 'w-radio');
        const i = el('input');
        i.type = 'radio';
        i.name = field.key;
        i.value = o.value;
        i.checked = values[field.key] === o.value;
        i.addEventListener('change', () => {
          values[field.key] = o.value;
          clearErr();
        });
        l.append(i, document.createTextNode(' ' + o.label));
        g.appendChild(l);
      }
      control = g;
      break;
    }
    case 'checkbox': {
      // Множественный выбор: группа чекбоксов по опциям, значение — массив.
      const g = el('div', 'w-checkbox-group');
      values[field.key] = (values[field.key] as string[]) ?? [];
      for (const o of optPairs(field.options)) {
        const l = el('label', 'w-checkbox');
        const i = el('input');
        i.type = 'checkbox';
        i.value = o.value;
        i.checked = (values[field.key] as string[]).includes(o.value);
        i.addEventListener('change', () => {
          const arr = (values[field.key] as string[]) ?? [];
          values[field.key] = i.checked ? [...arr, o.value] : arr.filter((x) => x !== o.value);
          clearErr();
        });
        l.append(i, document.createTextNode(' ' + o.label));
        g.appendChild(l);
      }
      control = g;
      break;
    }
    case 'rating': {
      const row = el('div', 'w-rating');
      const cur = Number(values[field.key]) || 0;
      for (let i = 1; i <= 5; i++) {
        const b = el('button', i <= cur ? 'w-star is-on' : 'w-star', '★');
        b.type = 'button';
        b.addEventListener('click', () => {
          values[field.key] = i;
          row.querySelectorAll('.w-star').forEach((s, j) => s.classList.toggle('is-on', j < i));
          clearErr();
        });
        row.appendChild(b);
      }
      control = row;
      break;
    }
    case 'file': {
      const box = el('div');
      const input = el('input', 'w-file');
      input.type = 'file';
      if (field.placeholder) input.title = field.placeholder;
      // Сам файл вернуть в input нельзя (безопасность браузера) — показываем имя выбранного.
      const chosen = el('div', 'w-file-name', (values[field.key] as string) || '');
      input.addEventListener('change', () => {
        const name = input.files?.[0]?.name ?? '';
        values[field.key] = name;
        chosen.textContent = name;
        clearErr();
      });
      box.append(input, chosen);
      control = box;
      break;
    }
    case 'phone': {
      const input = el('input', 'w-input');
      input.type = 'tel';
      input.inputMode = 'tel';
      input.autocomplete = 'tel';
      input.placeholder = field.placeholder || '+7 (___) ___-__-__';
      input.value = (values[field.key] as string) ?? '';
      input.addEventListener('input', () => {
        const masked = maskPhone(input.value);
        input.value = masked;
        try {
          input.setSelectionRange(masked.length, masked.length);
        } catch {
          /* setSelectionRange неприменим для type=tel в части браузеров */
        }
        values[field.key] = masked;
        clearErr();
      });
      control = input;
      break;
    }
    default: {
      const input = el('input', 'w-input');
      input.type = field.type === 'email' ? 'email' : 'text';
      if (field.type === 'email') input.inputMode = 'email';
      if (field.placeholder) input.placeholder = field.placeholder;
      input.value = (values[field.key] as string) ?? '';
      input.addEventListener('input', () => {
        values[field.key] = input.value;
        clearErr();
      });
      control = input;
    }
  }
  wrap.appendChild(control);
  if (field.hint) wrap.appendChild(el('div', 'w-hint', field.hint));
  return wrap;
}

function buildForm(ctx: BuildCtx, layout: 'card' | 'stepper'): BuildInstance {
  const c = (ctx.config.content ?? {}) as FormContent & { fields?: Field[] };
  const fields = (c.fields ?? []) as Field[];
  const values: Record<string, unknown> = {};
  const steps: Field[][] = layout === 'stepper' ? fields.map((f) => [f]) : [fields];
  let step = 0;

  const success = (): void => {
    clear(ctx.root);
    const box = el('div', 'w-form');
    const ok = el('div', 'w-success');
    ok.append(el('div', 'w-result-icon', '✓'), el('div', 'w-result-text', c.success_message ?? 'Спасибо! Скоро свяжемся.'));
    box.appendChild(ok);
    ctx.root.appendChild(box);
  };

  const error = (): void => {
    clear(ctx.root);
    const box = el('div', 'w-form');
    const err = el('div', 'w-error');
    err.append(el('div', 'w-result-icon', '✕'), el('div', 'w-result-text', 'Не удалось отправить. Попробуйте ещё раз.'));
    const retry = el('button', 'w-submit', 'Повторить');
    retry.type = 'button';
    retry.addEventListener('click', () => render());
    err.appendChild(retry);
    box.appendChild(err);
    ctx.root.appendChild(box);
  };

  const render = (): void => {
    clear(ctx.root);
    const form = el('form', 'w-form');
    // Кастомная валидация владеет всем: отключаем встроенную проверку браузера
    // (иначе невалидный type=email/tel молча блокирует submit и наши сообщения не видны).
    form.noValidate = true;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void advance();
    });
    const isStepper = layout === 'stepper';
    if (c.title && (!isStepper || step === 0)) form.appendChild(el('h2', undefined, c.title));
    if (c.subtitle && (!isStepper || step === 0)) form.appendChild(el('div', 'w-subtitle', c.subtitle));
    if (isStepper && steps.length > 1) {
      const p = el('div', 'w-progress');
      const fill = el('div', 'w-progress-fill');
      fill.style.width = `${Math.round(((step + 1) / steps.length) * 100)}%`;
      p.appendChild(fill);
      form.appendChild(p);
    }
    for (const f of steps[step] ?? []) form.appendChild(renderField(f, values));
    const nav = el('div', 'w-nav');
    if (isStepper && step > 0) {
      const back = el('button', 'w-back', 'Назад');
      back.type = 'button';
      back.addEventListener('click', () => { step--; render(); });
      nav.appendChild(back);
    }
    const last = !isStepper || step === steps.length - 1;
    const next = el('button', last ? 'w-submit' : 'w-next', last ? c.submit_label ?? 'Отправить' : 'Далее');
    next.type = 'submit';
    nav.appendChild(next);
    form.appendChild(nav);
    ctx.root.appendChild(form);
  };

  const advance = async (): Promise<void> => {
    const cur = steps[step] ?? [];
    let firstBad: string | null = null;
    for (const f of cur) {
      const msg = validateField(f, values[f.key]);
      setFieldError(ctx.root, f.key, msg);
      if (msg && !firstBad) firstBad = f.key;
    }
    if (firstBad) {
      ctx.root
        .querySelector<HTMLElement>(`[data-field-key="${firstBad}"] .w-input`)
        ?.focus();
      return;
    }
    if (layout === 'stepper' && step < steps.length - 1) { step++; render(); return; }
    try {
      await ctx.handlers.onSubmit?.({ fields: { ...values } });
      success();
    } catch {
      error();
    }
  };

  if (ctx.initialSuccess) success();
  else render();
  return {};
}

export const FORM_TEMPLATES: WidgetTemplate[] = [
  { id: 'form-card', type: 'form', build: (c) => buildForm(c, 'card') },
  { id: 'form-stepper', type: 'form', build: (c) => buildForm(c, 'stepper') },
  { id: 'form-callback', type: 'form', build: (c) => buildForm(c, 'card') },
];
